import { zodResolver } from '@hookform/resolvers/zod'
import React, { PropsWithChildren } from 'react'
import { DefaultValues, FieldValues, FormProvider, FormState, Mode, UseFormReturn, useForm } from 'react-hook-form'
import { ZodEffects, ZodObject, z } from 'zod'

import { FormField, FormFieldProps } from './field'

type AnyZodObject = ZodObject<any>
type AnyZodSchema = AnyZodObject | ZodEffects<AnyZodObject>

type FormProps<S extends AnyZodSchema, T extends FieldValues = z.infer<S>> = PropsWithChildren<{
  defaultValues?: DefaultValues<T>
  fields: {
    [K in keyof T]: FormFieldProps<T>
  }
  id?: string
  mode?: Mode
  name?: string
  reValidateMode?: Exclude<Mode, 'onTouched' | 'all'>
  schema: S
  shouldReset?: boolean // on defaultValues change
  onSubmit: (data: T) => void
  renderActions?: (formState: FormState<T>, formMethods: UseFormReturn<T>) => React.ReactNode
}>

export function Form<S extends AnyZodSchema, T extends FieldValues = z.infer<S>>({
  children,
  defaultValues,
  fields,
  id,
  mode = 'all',
  name,
  reValidateMode = 'onBlur',
  schema,
  shouldReset,
  onSubmit,
  renderActions,
}: FormProps<S, T>) {
  const methods = useForm<T>({
    defaultValues,
    mode,
    reValidateMode,
    resolver: zodResolver(schema as any),
  })

  const { reset } = methods
  React.useEffect(() => {
    if (!shouldReset) {
      return
    }

    reset(defaultValues)
  }, [defaultValues, shouldReset, reset])

  return (
    <FormProvider {...methods}>
      <form id={id} name={name} aria-label={name} onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        {Object.values<FormFieldProps<T>>(fields).map((field) => {
          return <FormField key={field.fieldProps.name} {...field} />
        })}
        {children}
        {renderActions && renderActions(methods.formState, methods)}
      </form>
    </FormProvider>
  )
}
